import React, { useState, useEffect } from 'react';
import { Search, Plus, Edit, Trash2, HelpCircle } from 'lucide-react';
import { adminService } from '../services/adminService';
import toast from 'react-hot-toast';

const FAQ = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingFaq, setEditingFaq] = useState(null);
  const [formData, setFormData] = useState({
    question: '',
    answer: '',
    category: 'general',
    order: 1,
  });

  useEffect(() => {
    fetchFAQs();
  }, []);

  const fetchFAQs = async () => {
    try {
      setLoading(true);
      const data = await adminService.getFaqs();
      
      if (Array.isArray(data)) {
        setFaqs(data);
        setError(null);
      } else {
        setFaqs([]);
        setError('Invalid data format received from server');
      }
    } catch (err) {
      setFaqs([]);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const mockFaqs = [
    {
      id: 1,
      question: 'How do I create a campaign?',
      answer: 'To create a campaign, go to the Campaigns section and click "Create Campaign". Fill in the required details and submit.',
      category: 'campaigns',
      order: 1,
    },
    {
      id: 2,
      question: 'What are the payment methods?',
      answer: 'We accept credit cards, PayPal, and bank transfers. All payments are processed securely.',
      category: 'payments',
      order: 2,
    },
  ];

  const displayFaqs = faqs.length > 0 ? faqs : mockFaqs;
  
  const filteredFaqs = displayFaqs.filter(faq => {
    const matchesSearch = (
      (faq.question && faq.question.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (faq.answer && faq.answer.toLowerCase().includes(searchTerm.toLowerCase()))
    );
    const matchesCategory = selectedCategory === 'all' || faq.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingFaq) {
        // Update existing FAQ
        await adminService.updateFaq(editingFaq.id, formData);
        toast.success('FAQ updated successfully!');
      } else {
        // Create new FAQ
        await adminService.createFaq(formData);
        toast.success('FAQ created successfully!');
      }
      
      setShowForm(false);
      setEditingFaq(null);
      setFormData({ question: '', answer: '', category: 'general', order: 1 });
      fetchFAQs();
    } catch (error) {
      toast.error(editingFaq ? 'Failed to update FAQ' : 'Failed to create FAQ');
    }
  };

  const handleEdit = (faq) => {
    setEditingFaq(faq);
    setFormData({
      question: faq.question,
      answer: faq.answer,
      category: faq.category,
      order: faq.order,
    });
    setShowForm(true);
  };

  const handleDelete = async (faqId) => {
    if (window.confirm('Are you sure you want to delete this FAQ?')) {
      try {
        await adminService.deleteFaq(faqId);
        toast.success('FAQ deleted successfully!');
        fetchFAQs();
      } catch (error) {
        toast.error('Failed to delete FAQ');
      }
    }
  };

  const categories = [
    { value: 'general', label: 'General' },
    { value: 'campaigns', label: 'Campaigns' },
    { value: 'payments', label: 'Payments' },
    { value: 'accounts', label: 'Accounts' },
    { value: 'technical', label: 'Technical' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">FAQ Management</h1>
          <p className="text-gray-600">Manage frequently asked questions</p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="btn-primary flex items-center gap-2"
        >
          <Plus className="h-4 w-4" />
          Add FAQ
        </button>
      </div>

      {showForm && (
        <div className="card p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            {editingFaq ? 'Edit FAQ' : 'Add New FAQ'}
          </h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Question</label>
                <input
                  type="text"
                  value={formData.question}
                  onChange={(e) => setFormData({ ...formData, question: e.target.value })}
                  className="input-field w-full"
                  placeholder="Enter the question"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="input-field w-full"
                >
                  {categories.map(cat => (
                    <option key={cat.value} value={cat.value}>{cat.label}</option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Answer</label>
              <textarea
                value={formData.answer}
                onChange={(e) => setFormData({ ...formData, answer: e.target.value })}
                rows={4}
                className="input-field w-full"
                placeholder="Enter the answer"
                required
              />
            </div>
            <div className="flex gap-3">
              <button type="submit" className="btn-primary">
                {editingFaq ? 'Update FAQ' : 'Create FAQ'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowForm(false);
                  setEditingFaq(null);
                  setFormData({ question: '', answer: '', category: 'general', order: 1 });
                }}
                className="btn-secondary"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="card p-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search FAQs..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input-field pl-10"
              />
            </div>
          </div>
          <div className="flex gap-3">
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="input-field"
            >
              <option value="all">All Categories</option>
              {categories.map(cat => (
                <option key={cat.value} value={cat.value}>{cat.label}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="card p-12 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading FAQs...</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredFaqs.map((faq) => (
            <div key={faq.id} className="card p-6">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {categories.find(cat => cat.value === faq.category)?.label || faq.category}
                    </span>
                    <span className="text-sm text-gray-500">Order: {faq.order}</span>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{faq.question}</h3>
                  <p className="text-gray-600">{faq.answer}</p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleEdit(faq)}
                    className="text-blue-600 hover:text-blue-900 p-1"
                    title="Edit"
                  >
                    <Edit className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(faq.id)}
                    className="text-red-600 hover:text-red-900 p-1"
                    title="Delete"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {!loading && filteredFaqs.length === 0 && (
        <div className="card p-12 text-center">
          <div className="mx-auto h-12 w-12 text-gray-400 mb-4">
            <HelpCircle className="h-12 w-12" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No FAQs found</h3>
          <p className="text-gray-500 mb-6">
            {searchTerm || selectedCategory !== 'all' 
              ? 'Try adjusting your search or filter criteria.'
              : 'No FAQs have been created yet.'
            }
          </p>
          <button
            onClick={() => setShowForm(true)}
            className="btn-primary"
          >
            <Plus className="h-4 w-4 mr-2" />
            Create First FAQ
          </button>
        </div>
      )}
    </div>
  );
};

export default FAQ;
